import { Navigate, useParams, Link } from 'react-router-dom';
import { getProductById, getProductsByCategory } from '../data/products';
import { getCategory } from '../data/categories';
import { company } from '../data/company';
import { downloadFile } from '../utils/utils';
import { useReveal } from '../hooks/useReveal';
import SpecTable from '../components/SpecTable';
import ProductCard from '../components/ProductCard';
import Breadcrumbs from '../components/Breadcrumbs';
import CTASection from '../components/CTASection';
import Icon from '../components/Icon';

export default function ProductDetail() {
  const { categorySlug = '', productId = '' } = useParams();
  const product = getProductById(productId);
  useReveal(productId);

  if (!product || product.categorySlug !== categorySlug) {
    return <Navigate to="/products" replace />;
  }

  const category = getCategory(product.categorySlug);
  const related = getProductsByCategory(product.categorySlug)
    .filter((p) => p.id !== product.id)
    .slice(0, 3);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary-deep text-white">
        <div className="container-page relative py-14 sm:py-20">
          <Breadcrumbs
            items={[
              { label: 'Home', to: '/' },
              { label: 'Products', to: '/products' },
              { label: category?.name ?? 'Category', to: `/products/${product.categorySlug}` },
              { label: product.code },
            ]}
          />
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <span className="rounded-md border border-white/20 bg-white/5 px-3 py-1 font-mono text-sm font-semibold">
              {product.code}
            </span>
            <span className="text-xs font-medium uppercase tracking-wider text-secondary-light">{product.group}</span>
          </div>
          <h1 className="mt-5 max-w-3xl text-3xl font-semibold leading-[1.08] sm:text-5xl">
            {product.name}
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-8 text-white/80">{product.description}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <button
              type="button"
              onClick={() => downloadFile(company.productGuide.fileUrl, company.productGuide.fileName)}
              className="btn-gold"
            >
              <Icon name="download" className="h-4 w-4" /> Download Product Guide
            </button>
            <Link to="/contact" className="btn border border-white/30 bg-white/5 text-white hover:bg-white/15">
              <Icon name="flask" className="h-4 w-4" /> Request a Sample
            </Link>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="container-page py-16 sm:py-20">
        <div className="grid gap-12 lg:grid-cols-[1.5fr_1fr] lg:items-start">
          <div className="reveal space-y-10">
            {/* Spec table */}
            <div>
              <h2 className="mb-4 text-xl font-bold text-text-primary">
                {product.specTable ? 'Typical Properties' : 'Typical Properties'}
              </h2>
              <SpecTable rows={product.specTable ?? product.typicalProperties} />
            </div>

            {/* Applications */}
            <div>
              <h2 className="mb-4 text-xl font-bold text-text-primary">Applications</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                {product.applications.map((a) => (
                  <div key={a} className="flex items-center gap-3 rounded-xl bg-surface p-4 ring-1 ring-ink/5">
                    <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-primary/8 text-primary">
                      <Icon name="check" className="h-5 w-5" />
                    </span>
                    <span className="text-sm font-medium text-text-primary">{a}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Key features aside */}
          <aside className="reveal lg:sticky lg:top-24">
            <div className="card p-7">
              <h2 className="text-lg font-bold text-text-primary">Key Features</h2>
              <ul className="mt-4 space-y-3">
                {product.keyFeatures.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-text-secondary">
                    <Icon name="check" className="mt-0.5 h-5 w-5 flex-none text-primary" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-6 rounded-xl bg-background p-4">
                <p className="text-xs font-semibold uppercase tracking-wider text-secondary-dark">
                  Need help specifying?
                </p>
                <p className="mt-1 text-sm text-text-secondary">
                  Our technical team can help you select the right grade for your formulation.
                </p>
                <Link to="/contact" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                  Contact us <Icon name="arrow" className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </aside>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-16 border-t border-ink/5 pt-12">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xl font-bold text-text-primary">More in {category?.name}</h2>
              <Link
                to={`/products/${product.categorySlug}`}
                className="inline-flex items-center gap-1 text-sm font-semibold text-primary"
              >
                View all <Icon name="arrow" className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </section>

      <CTASection />
    </div>
  );
}
